import React, { useEffect, useRef, useState } from 'react';
import { Logger } from '../utils';
import { Player } from '@lottiefiles/react-lottie-player';
import { useMediaSet } from 'use-media-set';
import AnimationPerformanceTest from './AnimationPerformanceTest';
import DeskTopBackGround from './WaitingRoomDeskTopBackground';
import PortraitBackGround from './WaitingRoomPortraitBackground';

import HotSpotPopover from './Popovers/HotSpotPopover';
import TipPopover from './Popovers/TipPopover';
import WelcomePopover from './Popovers/WelcomePopover';

import bswLandscape from '../../../../../lotties/bswLandscape.json';
import bswPortrait from '../../../../../lotties/bswPortrait.json';
import clinicLandscape from '../../../../../lotties/clinicLandscape.json';
import clinicPortrait from '../../../../../lotties/clinicPortrait.json';
import helicopterLandscape from '../../../../../lotties/helicopterLandscape.json';
import helicopterPortrait from '../../../../../lotties/helicopterPortrait.json';
import pharmacyLandscape from '../../../../../lotties/pharmacyLandscape.json';
import pharmacyPortrait from '../../../../../lotties/pharmacyPortrait.json';
import sunLandscape from '../../../../../lotties/sunLandscape.json';
import sunPortrait from '../../../../../lotties/sunPortrait.json';

const WaitingRoom = () => {
  const lottiePlayer = useRef(null);

  const [content, setContent] = useState('');
  const [hotSpot, setHotSpot] = useState('');
  const [showHotSpotPopover, setShowHotSpotPopover] = useState(false);
  const [showTipPopover, setShowTipPopover] = useState(false);
  const [showWelcomePopover, setShowWelcomePopover] = useState(false);

  const MAX_ANIMATION_DURATION = 5500; // milliseconds

  let performanceCheck = useRef(false);
  let lockHotSpot = useRef();

  useEffect(() => {
    performanceCheck.current = false;
    openWelcomePopover();
    setContent(
      <AnimationPerformanceTest
        performanceTestComplete={performanceTestComplete}
      />
    );
  }, []);

  const clickHotSpot = e => {
    if (lockHotSpot.current === false) {
      lockHotSpot.current = true;
      if (performanceCheck.current) {
        setShowHotSpotPopover(true);
      } else {
        toggleTipPopover();
      }
      setHotSpot(e.target.id);
      lockHotSpot.current = true;
    }

    Logger(
      'UI.Twilio.WaitingRoom.HotSpot.Click',
      new Date(),
      null,
      `${e.target.id}`
    );
  };

  const closeHotSpotPopover = () => {
    setShowHotSpotPopover(false);
    lockHotSpot.current = false;
    Logger(
      'UI.Twilio.WaitingRoom.HotSpotPopover.Close',
      new Date(),
      null,
      `${hotSpot}`
    );
  };

  const loadAnimation = animationName => {
    setContent(
      <div className="lottie-player-wrapper">
        <Player
          autoplay
          onEvent={event => {
            if (event === 'complete') {
              setContent(<WaitingRoomBackGround />);
            }
          }}
          ref={lottiePlayer}
          src={animationName}
          style={{ height: '100%', width: '100%' }}
        />
      </div>
    );
  };

  const performanceTestComplete = (animationStartTime, animationEndTime) => {
    const performanceTest =
      animationEndTime - animationStartTime < MAX_ANIMATION_DURATION;

    performanceCheck.current = performanceTest;

    Logger(
      `UI.Twilio.WaitingRoom.PassPerformanceCheck.${
        performanceCheck.current ? 'True' : 'False'
      }`,
      new Date()
    );

    setContent(<WaitingRoomBackGround />);
  };

  const playHotSpotAnimation = () => {
    let animationName;

    closeHotSpotPopover();
    switch (hotSpot) {
      case 'bswLandscape':
        animationName = bswLandscape;
        break;
      case 'bswPortrait':
        document
          .querySelector('#ambulatory-care-container')
          .scrollIntoView({ behavior: 'smooth' });
        animationName = bswPortrait;
        break;
      case 'clinicLandscape':
        animationName = clinicLandscape;
        break;
      case 'clinicPortrait':
        animationName = clinicPortrait;
        document
          .querySelector('#clinicPortrait')
          .scrollIntoView({ behavior: 'smooth' });
        break;
      case 'helicopterLandscape':
        animationName = helicopterLandscape;
        break;
      case 'helicopterPortrait':
        document
          .querySelector('#ambulatory-care-container')
          .scrollIntoView({ behavior: 'smooth' });
        animationName = helicopterPortrait;
        break;
      case 'pharmacyLandscape':
        animationName = pharmacyLandscape;
        break;
      case 'pharmacyPortrait':
        animationName = pharmacyPortrait;
        document
          .querySelector('#pharmacyPortrait')
          .scrollIntoView({ behavior: 'smooth' });
        break;
      case 'sunLandscape':
        animationName = sunLandscape;
        break;
      case 'sunPortrait':
        document
          .querySelector('#pharmacyPortrait')
          .scrollIntoView({ behavior: 'smooth' });
        animationName = sunPortrait;
        break;
      default:
        animationName = clinicLandscape;
    }
    loadAnimation(animationName);
    Logger(
      'UI.Twilio.WaitingRoom.PlayHotSpotAnimation',
      new Date(),
      null,
      `${hotSpot}`
    );
  };

  const closeTipPopover = () => {
    lockHotSpot.current = false;
    setShowTipPopover(false);
    Logger(
      'UI.Twilio.TipPopover.CloseIcon.Clicked',
      new Date(),
      null,
      `${hotSpot}`
    );
  };

  const closeWelcomePopover = () => {
    lockHotSpot.current = false;
    setShowWelcomePopover(false);
    Logger('UI.Twilio.WaitingRoom.WelcomePopover.GotIt.Clicked', new Date());
  };

  const openWelcomePopover = () => {
    lockHotSpot.current = true;
    setShowWelcomePopover(true);
    Logger('UI.Twilio.WaitingRoom.WelcomePopover.Open', new Date());
  };

  const toggleTipPopover = () => {
    closeHotSpotPopover();
    lockHotSpot.current = true;
    setShowTipPopover(!showTipPopover);
    Logger(
      `UI.Twilio.WaitingRoom.TipPopover.${showTipPopover ? 'Close' : 'Open'}`,
      new Date(),
      null,
      `${hotSpot}`
    );
  };

  const WaitingRoomBackGround = () => {
    const queries = {
      desktop: { minWidth: 1024 },
    };
    const mediaStates = useMediaSet(queries);
    const background = mediaStates.has('desktop') ? (
      <DeskTopBackGround clickHotSpot={clickHotSpot} />
    ) : (
      <PortraitBackGround clickHotSpot={clickHotSpot} />
    );

    return <>{background}</>;
  };

  return (
    <div className="waiting-room-container" id="waiting-room-container">
      {lockHotSpot.current === true && (
        <div className="waiting-room-popover-overlay"></div>
      )}

      <div id="welcome-popover-target"></div>

      {showHotSpotPopover && (
        <HotSpotPopover
          closeHotSpotPopover={closeHotSpotPopover}
          hotSpot={hotSpot}
          playHotSpotAnimation={playHotSpotAnimation}
          showHotSpotPopover={showHotSpotPopover}
          toggleTipPopover={toggleTipPopover}
        />
      )}

      {showTipPopover && (
        <TipPopover
          closeTipPopover={closeTipPopover}
          hotSpot={hotSpot}
          showTipPopover={showTipPopover}
        />
      )}

      {showWelcomePopover && (
        <WelcomePopover
          showWelcomePopover={showWelcomePopover}
          closeWelcomePopover={() => closeWelcomePopover()}
        />
      )}

      {content}
    </div>
  );
};

export default WaitingRoom;
